import {
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Church,
  CircleSlash,
  HandCoins,
  LineChart,
  ListChecks,
  Scale,
  Tags,
  Target,
  TrendingDown,
  TrendingUp,
  Trophy,
  UserCheck,
  Users,
  UsersRound,
  UserX,
  Wallet,
  type LucideIcon,
} from "lucide-react"

import { CONSOLIDATED_REPORT_TEMPLATE_IDS } from "@ibada/shared-types/reporting"

import { ACCOUNTING_ENTRIES } from "@/features/reports/registry/accounting-entries"
import {
  FinanceReportCategory,
  ReportBadge,
  ReportExportKind,
  ReportModule,
  type FinanceReportEntry,
} from "@/features/reports/model/report-registry-types"

/**
 * Curated Finance Report Catalog entries backed by ADR-0013 (Reporting Platform Foundation) reporting-engine templates. These are the
 * finance-relevant slice of the engine template catalog (giving, transactions, budgets, activity finance).
 * The membership and activity templates are curated in their own files, under their own modules — the
 * builder that used to hold them is retired. Title,
 * description and config derive from the engine (`reporting.templates.<id>.*`), so this file is curation
 * only — category, icon and search keywords.
 */
function engineEntry(
  id: string,
  category: FinanceReportCategory,
  icon: LucideIcon,
  keywords: string[],
  /** Which catalog tab. Finance by default — this file curates the finance slice of the engine catalog. */
  module: ReportModule = ReportModule.Finance,
): FinanceReportEntry {
  return {
    id,
    module,
    category,
    icon,
    titleKey: `reporting.templates.${id}.title`,
    descriptionKey: `reporting.templates.${id}.description`,
    keywords,
    dataSource: { kind: "engine-template", templateId: id },
    viewer: { layout: "result-panel", defaultView: "table" },
    export: { kind: ReportExportKind.Engine },
  }
}

export const ENGINE_ENTRIES: FinanceReportEntry[] = [
  // Transactions — money in vs out across the church.
  engineEntry("income-vs-expense-trend", FinanceReportCategory.Transactions, TrendingUp, ["income", "expense", "trend", "monthly", "net"]),
  engineEntry("net-balance-by-branch", FinanceReportCategory.Transactions, Scale, ["net", "balance", "branch", "surplus"]),
  engineEntry("expense-by-category", FinanceReportCategory.Transactions, TrendingDown, ["expense", "spending", "category"]),
  engineEntry("expense-by-branch", FinanceReportCategory.Transactions, Building2, ["expense", "spending", "branch"]),

  // Giving — contributions and pledges.
  engineEntry("income-by-category", FinanceReportCategory.Giving, HandCoins, ["income", "giving", "category", "offering"]),
  engineEntry("income-by-branch", FinanceReportCategory.Giving, Building2, ["income", "giving", "branch"]),
  engineEntry("income-category-trend", FinanceReportCategory.Giving, LineChart, ["income", "category", "trend", "monthly"]),
  engineEntry("contribution-by-group", FinanceReportCategory.Giving, Users, ["contribution", "giving", "group"]),
  engineEntry("contribution-by-ministry", FinanceReportCategory.Giving, Church, ["contribution", "giving", "ministry"]),
  engineEntry("top-contributors", FinanceReportCategory.Giving, Trophy, ["top", "contributors", "givers", "donors"]),
  engineEntry("pledge-fulfillment-by-activity", FinanceReportCategory.Giving, CalendarCheck, ["pledge", "fulfillment", "activity"]),
  engineEntry("pledge-fulfillment-by-group", FinanceReportCategory.Giving, Users, ["pledge", "fulfillment", "group"]),
  engineEntry("pledge-fulfillment-by-ministry", FinanceReportCategory.Giving, Church, ["pledge", "fulfillment", "ministry"]),
  engineEntry("pledge-fulfillment-by-branch", FinanceReportCategory.Giving, Building2, ["pledge", "fulfillment", "branch"]),
  engineEntry("unfulfilled-pledges-by-member", FinanceReportCategory.Giving, UserX, ["pledge", "unfulfilled", "outstanding", "member"]),
  engineEntry("fully-fulfilled-pledges-by-member", FinanceReportCategory.Giving, UserCheck, ["pledge", "fulfilled", "member"]),
  engineEntry("members-with-multiple-pledges", FinanceReportCategory.Giving, UsersRound, ["pledge", "members", "multiple"]),
  engineEntry("pledges-without-fulfillment", FinanceReportCategory.Giving, CircleSlash, ["pledge", "unfulfilled", "none"]),
  engineEntry("top-pledge-contributors", FinanceReportCategory.Giving, Trophy, ["top", "pledge", "contributors"]),
  engineEntry("pledges-by-contributor-type", FinanceReportCategory.Giving, Tags, ["pledge", "contributor", "type"]),
  engineEntry("outstanding-pledges-by-activity", FinanceReportCategory.Giving, CalendarClock, ["pledge", "outstanding", "activity"]),
  engineEntry("pledges-promised-vs-fulfilled-by-month", FinanceReportCategory.Giving, CalendarRange, ["pledge", "promised", "fulfilled", "monthly"]),

  // Budgets — planned allocations.
  engineEntry("budget-plan-by-branch", FinanceReportCategory.Budgets, Wallet, ["budget", "plan", "branch"]),
  engineEntry("budget-plan-by-activity", FinanceReportCategory.Budgets, Target, ["budget", "plan", "activity"]),
  engineEntry("budget-plan-by-status", FinanceReportCategory.Budgets, ListChecks, ["budget", "plan", "status"]),

  // Activities — program/event finance.
  engineEntry("finance-by-activity", FinanceReportCategory.Activities, CalendarDays, ["activity", "finance", "income", "expense"]),
  engineEntry("activities-by-revenue", FinanceReportCategory.Activities, TrendingUp, ["activity", "revenue", "income", "top"]),
  engineEntry("activities-by-expense", FinanceReportCategory.Activities, TrendingDown, ["activity", "expense", "spending", "top"]),
]

/**
 * The templates offered on the CONSOLIDATED (Global Finance) catalog.
 *
 * The list itself lives in shared-types so the backend can assert the same boundary — see
 * CONSOLIDATED_REPORT_TEMPLATE_IDS for the two rules that decide membership, and for why a report is judged by
 * the dimensions it selects rather than by what its id sounds like.
 */
export const CONSOLIDATED_ENGINE_ENTRY_IDS: ReadonlySet<string> = new Set(CONSOLIDATED_REPORT_TEMPLATE_IDS)

/** Engine templates that read correctly, and safely, consolidated across every branch. */
export const CONSOLIDATED_ENGINE_ENTRIES: FinanceReportEntry[] = ENGINE_ENTRIES.filter((entry) =>
  CONSOLIDATED_ENGINE_ENTRY_IDS.has(entry.id),
)

/**
 * The PARISH's own books, offered on the consolidated catalog under a badge.
 *
 * These are the accounting reports, and they were absent for a good reason: they resolve ONE branch, so a
 * workspace-titled trial balance covering the parish alone reads as a church-wide statement it is not. That
 * argument was about AMBIGUITY, not safety — and a badge answers it, where hiding them left a parish
 * treasurer with no route to their own journal except a URL nobody links to.
 *
 * Deliberately only the accounting set. The member, group and ministry templates are absent for a different
 * reason — they name people, and a badge does not make branch-private names church-wide reading.
 *
 * `chart-of-accounts` is left out too: the chart is workspace-owned, so labelling it "Parish" would be a
 * false statement about whose it is.
 */
const PARISH_ACCOUNTING_ENTRY_IDS: ReadonlySet<string> = new Set([
  "balance-sheet",
  "income-statement",
  "cash-flow",
  "trial-balance",
  "general-ledger",
  "journal-entries",
])

/**
 * Accounting reports as they appear on the CONSOLIDATED catalog: identical entries, badged Parish.
 *
 * The badge is added here rather than on the entry itself because the same report is unbadged on the
 * parish's own catalog, where nothing else is church-wide and there is nothing to distinguish it from.
 */
export const PARISH_ACCOUNTING_ENTRIES: FinanceReportEntry[] = ACCOUNTING_ENTRIES.filter((entry) =>
  PARISH_ACCOUNTING_ENTRY_IDS.has(entry.id),
).map((entry) => ({ ...entry, badge: ReportBadge.Parish }))
